// The look of the faction club (casino.md §16.1, R2165).
//
// The owner picked card 04 "The Faction Club" of mockup round 101: the
// casino is the club of the faction that holds the most cells, and the
// room takes the name of the charge on that faction's badge. The server
// states the club (`CasinoLobby.club_faction`, R2144); this module draws
// it and holds no state.
//
// **The club ink rides the identity marks alone**, as the team ink of an
// arcade match does (R2051): the badge, the rule of the club head, the
// lead of the roster bar, the game marks of the lobby, the rim of the slot
// symbols and the rim of the wheel. Every control keeps the reader's own
// accent. The ink reaches the sheet as `--club` and `--club-rgb` on the
// casino host (`clubStyle`), so a change of the club is one write and no
// mark is built again. A pocket and a suit keep the casino inks (R2118):
// neither the club ink nor `--bad` ever paints one.
//
// **The house line is honest** (R990, the owner's ruling of 2026-10-14):
// the share comes from `CasinoRules.club_share`, and a line promises a
// share only while the club window holds a gain. A window with a loss says
// so, and the next turn of the light stands as a time of the world clock.
//
// **No club** (`club_faction` 0: no faction holds a cell): the casino
// wears the house look under its plain name, the head of the tab
// (`casino_head`), with no badge, no roster and no share line, and the
// marks take the dim ink.

import { CASINO_CLUB_BADGE_PX, CASINO_ROSTER_LABEL_PCT, CASINO_SEAT_BADGE_PX } from '../../gen/presentation';
import { DAWN_HOUR, DUSK_HOUR } from '../../gen/balance';
import type { CasinoLobby, CasinoRules } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { credits } from '../../arcade/format';
import { pad2 } from '../../locale/stamp';
import { factionColor, factionName, factionOf, hex } from '../../world';
import { badgePicture } from '../chart/faction-key';
// The room name of the club faction lives in `ui/clubroom.ts`, which the
// INBOX reads too (R2144).
import { roomName } from '../clubroom';
import { teamTokens } from '../theme';
import { pct } from './pct';
import { secOfDay } from '../../locale/clock';

/** The faction that holds the club, or 0 while none does. A faction id
 *  the roster does not know holds no club this client can draw. */
export function clubOf(lobby: CasinoLobby | null): number {
  const id = lobby?.clubFaction ?? 0;
  return id > 0 && factionOf(id) ? id : 0;
}

/**
 * The world hour of the next turn of the light after `clockSec`, the world
 * time of day: dawn before dawn, dusk in the day, and the dawn of the next
 * day after dusk. The hours are `[world.day]` of the compile-time lane,
 * the one pair the hub pays the club share on (casino.md §16).
 */
export function nextLightHour(clockSec: number, dawn = DAWN_HOUR, dusk = DUSK_HOUR): number {
  const hour = secOfDay(clockSec) / 3600;
  if (hour < dawn) return dawn;
  if (hour < dusk) return dusk;
  return dawn;
}

/** One world hour as the time a reader reads on the world clock, `18:00`. */
export function hourText(hour: number): string {
  const h = Math.floor(hour);
  return `${pad2(h)}:${pad2(Math.round((hour - h) * 60))}`;
}

/** What the club share would be at the turn of the light if the window
 *  closed now: the share of a positive net, rounded down, plus the carried
 *  remainder, as the hub reckons it (`payClub`). 0 for a window with no
 *  gain. */
export function clubSoFar(lobby: CasinoLobby, rules: CasinoRules): number {
  if (lobby.clubNet <= 0) return 0;
  return Math.floor(lobby.clubNet * rules.clubShare) + lobby.clubCarry;
}

/** The share as a percent figure in the reader's language. */
function sharePct(rules: CasinoRules): string {
  return pct(rules.clubShare * 100);
}

/**
 * The style of the casino host for one club: the club ink on `--club` and
 * `--club-rgb`, the tokens the team ink of an arcade match uses. No club
 * gives '' and the sheet's fallbacks draw the house look.
 */
export function clubStyle(faction: number): Record<string, string> {
  if (faction <= 0) return {};
  const t = teamTokens(faction);
  return { '--club': t['--team'], '--club-rgb': t['--team-rgb'] };
}

/** The badge of one faction, painted once a size, or '' for none. */
export function factionBadge(faction: number, px: number): string {
  return faction > 0 ? badgePicture(faction, 'm', px) : '';
}

/** The badge a casino seat wears beside its name (R2165). A fictive seat
 *  carries faction 0 and wears none. */
export function seatBadge(faction: number): string {
  const pic = factionBadge(faction, CASINO_SEAT_BADGE_PX);
  return pic === '' ? '' : `<span class="seat-badge" aria-hidden="true">${pic}</span>`;
}

/**
 * The roster bar: one segment a faction that holds a cell, as wide as its
 * share of the cells, in the faction ink with the faction badge, the
 * second channel of R1568. The club faction's segment carries the lead
 * rim. The count of segments comes from `club_cells`, never from a fixed
 * three or five. A world whose factions hold no cell draws no bar.
 */
function rosterHtml(lobby: CasinoLobby, club: number): string {
  const rows: { id: number; n: number }[] = [];
  lobby.clubCells.forEach((n, i) => {
    const id = i + 1;
    if (n > 0 && factionOf(id)) rows.push({ id, n });
  });
  const total = rows.reduce((a, r) => a + r.n, 0);
  if (total <= 0) return '';
  const parts: string[] = [];
  const segs = rows.map((r) => {
    const share = Math.round((r.n * 100) / total);
    parts.push(S.casino_roster_part({ faction: factionName(r.id), pct: String(share) }));
    const figure = share >= CASINO_ROSTER_LABEL_PCT ? `<span class="rp">${share}%</span>` : '';
    const ink = hex(factionColor(r.id));
    return `<span class="rs${r.id === club ? ' lead' : ''}" style="flex-grow:${r.n};--fk:${ink}">`
      + `${factionBadge(r.id, CASINO_SEAT_BADGE_PX)}${figure}</span>`;
  }).join('');
  return `<div class="roster" role="img" aria-label="${S.casino_roster_label({ parts: parts.join(', ') })}">${segs}</div>`;
}

/** The share line of the club head: the gain, the loss or no net yet. */
function shareLine(lobby: CasinoLobby, rules: CasinoRules, room: string, clockSec: number): string {
  const time = hourText(nextLightHour(clockSec));
  if (lobby.clubNet > 0) {
    const sum = clubSoFar(lobby, rules);
    const each = lobby.clubMembers > 0 ? Math.floor(sum / lobby.clubMembers) : 0;
    return `<div class="club-share gain">${S.casino_club_gain({
      time, pct: sharePct(rules), room, amount: credits(sum), each: credits(each),
    })}</div>`;
  }
  if (lobby.clubNet < 0) {
    return `<div class="club-share loss">${S.casino_club_loss({
      amount: credits(-lobby.clubNet), time, room,
    })}</div>`;
  }
  return `<div class="club-share">${S.casino_club_even({ time })}</div>`;
}

/**
 * The head of the lobby. With a club: the badge, the room, the club
 * faction and its commanders, the roster bar and the share line. With no
 * club: the house look, the line that says why under the plain name the
 * tab head already states, so the name stands once.
 */
export function clubHeadHtml(lobby: CasinoLobby, rules: CasinoRules, clockSec: number): string {
  const club = clubOf(lobby);
  if (club === 0) {
    return '<div class="club-head house">'
      + `<div class="club-id"><div class="club-of">${S.casino_house_of}</div></div></div>`;
  }
  const room = roomName(club);
  return `<div class="club-head" data-club="${club}">`
    + `<span class="club-badge" aria-hidden="true">${factionBadge(club, CASINO_CLUB_BADGE_PX)}</span>`
    + `<div class="club-id"><div class="club-title">${S.casino_room_title({ room })}</div>`
    + `<div class="club-of">${S.casino_club_of({ faction: factionName(club), members: lobby.clubMembers })}</div></div>`
    + rosterHtml(lobby, club)
    + shareLine(lobby, rules, room, clockSec)
    + '</div>';
}

/**
 * The club half of one lobby row's house line (R990): where the share of
 * what the house keeps goes. It promises a share only while the club
 * window holds a gain. No club gives ''.
 */
export function clubRowLine(lobby: CasinoLobby, rules: CasinoRules): string {
  const club = clubOf(lobby);
  if (club === 0) return '';
  const room = roomName(club);
  if (lobby.clubNet > 0) return S.casino_club_row_gain({ pct: sharePct(rules), room });
  if (lobby.clubNet < 0) return S.casino_club_row_loss({ room });
  return S.casino_club_row_even({ room });
}
